import { defaultFeature, useClient, useMutation, useQuery } from '@weenat/client'
import { FeaturKey } from '@weenat/client/dist/FeatureToggle'
import { canUser, ops } from '@weenat/client/dist/core/access'
import { OrgFeatureToggle } from '@weenat/client/dist/resources/orgFeatureToggle'
import { Redirect } from 'app/routx-router'
import Button from 'app/src/kit/Button'
import Card from 'app/src/kit/Card'
import Text from 'app/src/kit/Text'
import { TextFieldPrimitive } from 'app/src/kit/fields/TextField'
import AuthenticatedLayout from 'app/src/layouts/AuthenticatedLayout'
import ProtectedRoute from 'app/src/layouts/ProtectedRoute'
import { useLoginAs } from 'app/state'
import { useUserContext } from 'app/userProvider'
import { isEmpty, isNil } from 'lodash-es'

const RolloutDeployment: FC = () => {
  const client = useClient()
  const [mutationResult, mutationState] = useMutation(client.orgFeatureToggle.update())
  const { me } = useUserContext()
  const [loginAs] = useLoginAs()
  const canView = canUser(ops.view, 'debugMode', { account: me }) || loginAs !== null
  const allOverride = useQuery(client.orgFeatureToggle.get(), { enabled: canView }).data
  if (!canView) return <Redirect to='/' />
  return (
    <Box $backgroundColor='grayscale.white' $width={'100%'} $p={'md'} $overflow='auto'>
      <Flex $flexDirection='column' $gap='md' $p={'md'}>
        <Text $fontWeight='bold'>Roll out deployment configuration</Text>
        <Text>Each field should contains network ids and organizations ids separated by ,</Text>
        <form
          onSubmit={(e) => {
            e.preventDefault() /* Handle form submission */
            const data = new FormData(e.currentTarget)
            const version = data.get('version')
            if (!isNil(version) && typeof version === 'string' && !isEmpty(version)) {
              const payload: Partial<OrgFeatureToggle> & { version: string } = { version }
              Object.keys(defaultFeature).forEach((key) => {
                const castedKey = key as FeaturKey
                const networkIdsKey = `${key}_networkIds` as const
                const orgIdsKey = `${key}_orgIds` as const
                const networkIdsValue = data.get(networkIdsKey)
                const orgIdsValue = data.get(orgIdsKey)

                payload[castedKey] = {
                  networkIds:
                    typeof networkIdsValue === 'string' && networkIdsValue.length > 0
                      ? (networkIdsValue
                          .split(',')
                          .map((stringVal) => Number.parseInt(stringVal.replace(' ', ''), 10)) ??
                        [])
                      : [],
                  orgIds:
                    typeof orgIdsValue === 'string' && orgIdsValue.length > 0
                      ? (orgIdsValue
                          .split(',')
                          .map((stringVal) => Number.parseInt(stringVal.replace(' ', ''), 10)) ??
                        [])
                      : []
                }
              })

              mutationResult(payload)
            }
          }}
        >
          <Flex $flexDirection='column' $gap='md' $p={'md'} $maxWidth={600} $mr='auto'>
            {Object.keys(defaultFeature).map((key) => {
              const castedKey = key as FeaturKey
              return (
                <Card key={key} $gap='sm'>
                  <Text $fontWeight='semiBold'>{key}</Text>
                  <TextFieldPrimitive
                    label={`network ids `}
                    type='text'
                    name={`${key}_networkIds`}
                    defaultValue={allOverride?.[castedKey]?.networkIds?.join(',') ?? ''}
                    isRequired={false}
                  />
                  <TextFieldPrimitive
                    label={`organizations ids`}
                    type='text'
                    name={`${key}_orgIds`}
                    defaultValue={allOverride?.[castedKey]?.orgIds?.join(',') ?? ''}
                    isRequired={false}
                  />
                </Card>
              )
            })}
            <TextFieldPrimitive
              label='minimal version'
              type='text'
              name='version'
              defaultValue={allOverride?.version ?? ''}
            />
          </Flex>
          <Button type='submit' state={mutationState}>
            Publish
          </Button>
        </form>
      </Flex>
    </Box>
  )
}

export const Component = () => {
  return (
    <ProtectedRoute>
      <AuthenticatedLayout>
        <RolloutDeployment />
      </AuthenticatedLayout>
    </ProtectedRoute>
  )
}
export default Component
